import { Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges } from '@angular/core';
import * as dayjs from 'dayjs';
import { Observable, Subscription, Subject } from 'rxjs';
import { takeUntil, finalize } from 'rxjs/operators';
import Swiper, { SwiperOptions } from 'swiper';
import { IHolidayLive } from '../../../holiday-booking-page/interfaces/iholiday-live';
import { IHolidayLiveParams } from '../../../holiday-booking-page/interfaces/iholiday-live-params';
import { HolidayBookingService } from '../../../holiday-booking-page/services/holiday-booking.service';
import { BookingService } from '../../../../../shared/services/booking.service';
import { HolidayImagesService } from '../../../../../shared/services/holiday-images.service';
import { HolidayCard } from '../../interfaces/holiday-card';
import { HolidaySolution } from '../../interfaces/holiday-solution';
import { NavigationService } from '../../../../../../services/navigation.service';
import { HolidayDetailUrlPipe } from '../../../../../shared/pipes/holiday-detail-url.pipe';
import {HolidayDetailService} from '../../../holiday-detail-page/services/holiday-detail.service';
import {ModalService} from '../../../../../../services/modal.service';
import {CmsService} from '../../../../../cms/services/cms.service';
import {Networks} from '../../../../../shared/models/networks.enum';
import { HolidaySearchFilterService } from '../../services/holiday-search-filter.service';

@Component({
  selector: 'app-holiday-search-card',
  templateUrl: './holiday-search-card.component.html',
  styleUrls: ['./holiday-search-card.component.scss']
})
export class HolidaySearchCardComponent implements OnInit, OnDestroy {

  @Input() holiday: HolidayCard;
  @Input() ages: number[];

  images$: Observable<string[]>;
  isWidget: boolean;
  swiper: Swiper;
  holidayNetQuote: IHolidayLive;
  isLoadingNetQuote: boolean;
  hasNetQuoteError: boolean;

  public showSpecialLogo: boolean;

  holidayNetQuoteSubscription: Subscription;

  public brand: any;
  public networks = Networks;

  public availableNights: number[];

  private brandConfigSub: Subscription;

  config: SwiperOptions = {
    navigation: {
      nextEl: '.swiper-button-next',
      prevEl: '.swiper-button-prev',
    }
  };

  index;
  packetInfo: any;

  private unsubscribe$ = new Subject;

  constructor(
    protected _holidayImagesService: HolidayImagesService,
    protected _holidayBookingService: HolidayBookingService,
    protected _navigationService: NavigationService,
    protected _holidayDetailService: HolidayDetailService,
    protected _modalService: ModalService,
    protected _bookingService: BookingService,
    protected _cmsService: CmsService,
    protected _holidaySearchFilterService: HolidaySearchFilterService
  ) { }

  ngOnInit() {
    this.showSpecialLogo = this.setShowabilitySpecialLogo();
    this.brandConfigSub = this._cmsService.brandConfig$.subscribe(brand => {
      if (brand) { this.brand = brand; }
    });
    if (this.holiday.isCustom && this.holiday.bestSolution?.hasNetQuote) {
      this.showNetQuote(this.holiday.bestSolution);
    }

    this.setAvailableNightsNights();

    this._holidaySearchFilterService.updateTrigger.pipe(
        takeUntil(this.unsubscribe$)
      ).subscribe( _ => {
      if (this.holiday.isCustom && !this.holiday.wsHoliday && this.holiday.bestSolution?.hasNetQuote) {
        setTimeout( () => {
          this.showNetQuote(this.holiday.bestSolution);
        }, 50);
      }
    });

  }

  ngOnDestroy() {
    this.brandConfigSub?.unsubscribe();
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  private setShowabilitySpecialLogo() {
    return this.holiday.realTourOperatorId == 1016058 || this.holiday.wsRealTourOperatorId == 1016058;
  }

  private setAvailableNightsNights(): void {
    this.availableNights = this.holiday.solutions
      .map( (solution: HolidaySolution) => solution.nights )
      .filter( (numberOfNights: number, index: number, selfArray: number[]) => {
        return numberOfNights && selfArray.indexOf(numberOfNights) === index;
      })
      .sort( (nightsA: number, nightsB: number) => nightsA - nightsB);
  }

  public onClickOnNightsPill(nights: number): void {
    this.holiday.bestSolution = this.holiday.solutions
      .find( (solution: HolidaySolution) => {
        return solution.nights === nights;
      });
  }

  goToDetail(holiday): void {
    this._navigationService.goTo(this.getDetailUrl(holiday));
  }

  getDetailUrl(holiday): string{
    const duration = holiday.bestSolution.nights;
    const url = '.' + new HolidayDetailUrlPipe().transform(this.holiday, this.ages, duration);
    return url;
  }

  setSwiperInstance($event: Swiper): void {
    this.swiper = $event;
  }

  getPhotos(id: number, type?: string): Observable<string[]> {
    return this._holidayImagesService.getHolidayPhotos(id, type).pipe(
      // finalize(() => this.swiper.slideNext())
    );
  }

  showMoreImages(): void {
    if (this.images$) { return; }
    this.images$ = this.holiday.accomodation ?
      this.getPhotos(this.holiday.accomodation.id) : this.getPhotos(this.holiday.tour.id, 'tours');
  }

  showNetQuote(solution: HolidaySolution): void {
    const ages = this._bookingService.getAgesArray();
    this.hasNetQuoteError = false;
    this.isLoadingNetQuote = true;

    this.holidayNetQuoteSubscription =
      this._holidayBookingService.getFormattedHolidayLive(this.createNetQuoteRequestParams(this.holiday, solution, ages))
        .pipe(
          takeUntil(this.unsubscribe$),
          finalize(() => this.isLoadingNetQuote = false)
        ).subscribe(e => {
        if (e) {
          this.holidayNetQuote = e;
        } else {
          this.hasNetQuoteError = true;
        }
      });
  }

  createNetQuoteRequestParams(holiday: HolidayCard, solution: HolidaySolution, ages: number[]): IHolidayLiveParams {
    return {
      request: {
        pluginSource: String(holiday.pluginSource),
        ages,
        departureDate: dayjs(solution.dateFrom).format('YYYY-MM-DD'),
        departurePlace: solution.airport,
        idSolution: parseInt(solution.id, 10) || null,
        sourceId: solution.source,
        topId: holiday.tourOperator,
        onlyNetQuotes: true
      }
    };
  }

  getPacketDetail(solution: HolidaySolution): void {
    this._holidayDetailService.getPacketDetail(solution?.packet).pipe().toPromise().then((value) => {
      this.packetInfo = value;
      this._modalService.openModal('packet-detail',  this.packetInfo);
    });
  }

}
